import { ObserversModule } from '@angular/cdk/observers';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';

import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { RouterModule } from '@angular/router';
import { AuthModule } from '@app/auth/auth.module';
import { ComponentDependencyJunctionService } from '@app/core/services/component-dependency-junction.service';
import { BlockContainerComponent } from '@modules/creator/components/block-container/block-container.component';
import { FormFieldFileComponent } from '@modules/creator/components/forms/form-field/form-field-file.component';
import { FormFieldSearchComponent } from '@modules/creator/components/forms/form-field/form-field-search.component';
import { FormFieldSelectComponent } from '@modules/creator/components/forms/form-field/form-field-select.component';
import { FormFieldTextareaComponent } from '@modules/creator/components/forms/form-field/form-field-textarea.component';
import { HeadingBlockComponent } from '@modules/creator/components/heading-block/heading-block.component';
import { IframeComponent } from '@modules/creator/components/iframe/iframe.component';
import { ImageBlockComponent } from '@modules/creator/components/image-block/image-block.component';
import { LinkBlockComponent } from '@modules/creator/components/link-block/link-block.component';
import { MainMenuService } from '@modules/creator/components/main-menu/services/main-menu.service';

// popup
import { PopupService } from '@modules/creator/components/popup/services/popup.service';
import { SnippetBlockComponent } from '@modules/creator/components/snippet-block/snippet-block.component';
import {
  AdvancedFiltersComponent
} from '@modules/creator/components/special-block/advanced-filters/advanced-filters.component';
import {
  ActiveFilterPreviewComponent
} from '@modules/creator/components/special-block/advanced-filters/components/active-filter-preview/active-filter-preview.component';
import {
  AdvancedFilterGroupSelectorComponent
} from '@modules/creator/components/special-block/advanced-filters/components/advanced-filter-group-selector/advanced-filter-group-selector.component';
import {
  AdvancedFilterSelectItemComponent
} from '@modules/creator/components/special-block/advanced-filters/components/advanced-filter-group-selector/advanced-filter-select-item.component';
import {
  FilterRangeComponent
} from '@modules/creator/components/special-block/advanced-filters/components/filter-range/filter-range.component';
import { TextCollapseComponent } from '@modules/creator/components/text-collapse/text-collapse.component';
import { ArtistListComponent } from '@modules/creator/components/special-block/artist-list/artist-list.component';
import {
  ExhibitionListComponent,
} from '@modules/creator/components/special-block/exhibition-list/exhibition-list.component';
import {
  ArtworkRequestFormComponent,
} from '@modules/creator/components/special-block/forms/artwork-request-form/artwork-request-form.component';
import {
  ContactFormComponent,
} from '@modules/creator/components/special-block/forms/contact-form/contact-form.component';
import { FormService } from '@modules/creator/components/special-block/forms/services/form.service';
import {
  LanguageSwitcherComponent,
} from '@modules/creator/components/special-block/language-switcher/language-switcher.component';
import {
  CurrencySwitcherComponent,
} from '@modules/creator/components/special-block/currency-switcher/currency-switcher.component';
import {
  LightboxBasicViewComponent,
} from '@modules/creator/components/special-block/lightbox/lightbox-basic-view/lightbox-basic-view.component';
import {
  LightboxPreviewImageComponent,
} from '@modules/creator/components/special-block/lightbox/lightbox-preview-view/lightbox-preview-image/lightbox-preview-image.component';
import {
  LightboxPreviewViewComponent,
} from '@modules/creator/components/special-block/lightbox/lightbox-preview-view/lightbox-preview-view.component';

import { LightboxComponent } from '@modules/creator/components/special-block/lightbox/lightbox.component';
import { LightboxService } from '@modules/creator/components/special-block/lightbox/services/lightbox.service';
import { ListComponent } from '@modules/creator/components/special-block/list/list.component';

// my account
import {
  MyAccountDropdownComponent,
} from '@modules/creator/components/special-block/my-account-dropdown/my-account-dropdown.component';
import { NewsListComponent } from '@modules/creator/components/special-block/news-list/news-list.component';
import { PaginationComponent } from '@modules/creator/components/special-block/pagination/pagination.component';
import { SvgBlockComponent } from '@modules/creator/components/svg-block/svg-block.component';
import { SafeHtmlPipe } from '@modules/creator/pipes/safe-resource-url.pipe';
import { EditableBlockDirective } from '@modules/editor/pages/directive/editable-block.directive';
import { EditableComponentDirective } from '@modules/editor/pages/directive/editable-component.directive';
import { EditableImageDirective } from '@modules/editor/pages/directive/editable-image.directive';
import { EditableTextDirective } from '@modules/editor/pages/directive/editable-text.directive';
import {
  LoadersWrapperSmallComponent,
} from '@modules/loaders/components/loaders-wrapper-small/loaders-wrapper-small.component';

import { MaterialModule } from '@modules/material/material.module';
import { TranslateModule } from '@ngx-translate/core';
import { EllipsisModule } from 'ngx-ellipsis';
import { EditableIframeDirective } from '../editor/pages/directive/editable-iframe.directive';
import { BlockComponent } from './components/block/block.component';
import { CookiesAlertComponent } from './components/cookies-alert/cookies-alert.component';

import { FooterComponent } from './components/footer/footer.component';
import { CheckboxFieldComponent } from './components/forms/form-field/checkbox-field.component';

import { FormFieldComponent } from './components/forms/form-field/form-field.component';
import { PasswordFieldComponent } from './components/forms/form-field/password-field.component';
import { IframeCodeComponent } from './components/iframe/iframe-code/iframe-code.component';
import { MainMenuDesktopComponent } from './components/main-menu/main-menu-desktop/main-menu-desktop.component';
import { MainMenuTabletComponent } from './components/main-menu/main-menu-tablet/main-menu-tablet.component';
import { MainMenuComponent } from './components/main-menu/main-menu.component';

import { MediaComponent } from './components/media/media.component';
import { PopupComponent } from './components/popup/popup.component';
import { ScryptZMailerLite } from './components/scrypt/scriptLoader.service';
import { ScryptComponent } from './components/scrypt/scrypt.component';
import { SpecialBlockContainerComponent } from './components/special-block-container/special-block-container.component';
import { ButtonComponent } from './components/special-block/button/button.component';

// forms components
import { LoginFormComponent } from './components/special-block/forms/login-form/login-form.component';
import {
  PasswordResetFormComponent,
} from './components/special-block/forms/password-reset-form/password-reset-form.component';
import { RegisterFormComponent } from './components/special-block/forms/register-form/register-form.component';
import {
  GlobalSearchInputComponent,
} from './components/special-block/global-search-input/global-search-input.component';
import { GlobalSearchListComponent } from './components/special-block/global-search-list/global-search-list.component';
import { InventoryListComponent } from './components/special-block/inventory-list/inventory-list.component';
// import { CoreModule } from '@app/core/core.module';
import {
  LightboxPreviewVideoComponent,
} from './components/special-block/lightbox/lightbox-preview-view/lightbox-preview-video/lightbox-preview-video.component';
import {
  FilterGroupSelectorOverlayComponent,
} from './components/special-block/list-filters/components/filter-group-selector/filter-group-selector-overlay/filter-group-selector-overlay.component';
import {
  FilterGroupSelectorComponent,
} from './components/special-block/list-filters/components/filter-group-selector/filter-group-selector.component';
import {
  FilterSelectItemComponent,
} from './components/special-block/list-filters/components/filter-group-selector/filter-select-item.component';
import {
  FilterSearchInputComponent,
} from './components/special-block/list-filters/components/filter-search-input/filter-search-input.component';
import {
  FilterSingleSelectorComponent,
} from './components/special-block/list-filters/components/filter-single-selector/filter-single-selector.component';
import {
  FilterSlidableComponent,
} from './components/special-block/list-filters/components/filter-slidable/filter-slidable.component';
import { ListFiltersComponent } from './components/special-block/list-filters/list-filters.component';
import { PaginatorViewComponent } from './components/special-block/pagination/paginator-view.component';
import { HasFeatureDirective } from '@shared/directives/has-feature.directive';

import { PriceConfiguratorComponent } from './components/special-block/price-configurator/price-configurator.component';
import { ShoppingCartComponent } from './components/special-block/shopping-cart/shopping-cart.component';
import { SliderComponent } from './components/special-block/slider/slider.component';
import { SortingListComponent } from './components/special-block/sorting-list/sorting-list.component';

import { SpecialBlockComponent } from './components/special-block/special-block.component';
import { WallCompositionComponent } from '@modules/wall-visualization/wall-composition.component';
import { WallPreviewComponent } from '@modules/wall-visualization/wall-preview.component';

// import directives
import {
  CanShowDirective,
  ClickableBlockDirective,
  FilterActiveDirective,
  ParentStyleEmptyDirective,
  RouterLinkActiveParentDirective,
  SubmenuHoverDirective,
} from './directives';
import { SnippetPipe } from './pipes/snippet.pipe';

const directives = [
  ClickableBlockDirective,
  RouterLinkActiveParentDirective,
  CanShowDirective,
  SubmenuHoverDirective,
  FilterActiveDirective,
  ParentStyleEmptyDirective,
];

const filters = [
  FilterSearchInputComponent,
  FilterGroupSelectorComponent,
  FilterSelectItemComponent,
  FilterSingleSelectorComponent,
];

const pipes = [SnippetPipe];

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ReactiveFormsModule,
    MaterialModule,
    AuthModule,
    TranslateModule,
    LoadersWrapperSmallComponent,
    EllipsisModule,
    ObserversModule,
    MatSliderModule,
    EditableComponentDirective,
    EditableTextDirective,
    EditableBlockDirective,
    EditableIframeDirective,
    EditableImageDirective,
    HeadingBlockComponent,
    ScryptComponent,
    IframeCodeComponent,
    SafeHtmlPipe,
    IframeComponent,
    HasFeatureDirective,
    WallCompositionComponent,
  ],
  declarations: [
    FooterComponent,
    BlockComponent,
    BlockContainerComponent,
    SpecialBlockComponent,
    SpecialBlockContainerComponent,
    ButtonComponent,
    CookiesAlertComponent,
    InventoryListComponent,
    ListFiltersComponent,
    ExhibitionListComponent,
    NewsListComponent,
    ArtistListComponent,
    ListComponent,
    MainMenuComponent,
    MainMenuDesktopComponent,
    MainMenuTabletComponent,
    LinkBlockComponent,
    PaginationComponent,
    SliderComponent,
    LightboxComponent,
    LightboxBasicViewComponent,
    LightboxPreviewViewComponent,
    LightboxPreviewImageComponent,
    PopupComponent,
    MediaComponent,
    ImageBlockComponent,
    SnippetBlockComponent,
    TextCollapseComponent,
    SvgBlockComponent,
    MyAccountDropdownComponent,
    ShoppingCartComponent,
    PriceConfiguratorComponent,
    RegisterFormComponent,
    LoginFormComponent,
    PasswordResetFormComponent,
    ContactFormComponent,
    ArtworkRequestFormComponent,
    FormFieldComponent,
    FormFieldSearchComponent,
    FormFieldTextareaComponent,
    PasswordFieldComponent,
    CheckboxFieldComponent,
    LanguageSwitcherComponent,
    CurrencySwitcherComponent,
    GlobalSearchInputComponent,
    GlobalSearchListComponent,
    ...filters,
    ...directives,
    ...pipes,
    PaginatorViewComponent,
    FilterGroupSelectorOverlayComponent,
    FilterSlidableComponent,
    LightboxPreviewVideoComponent,
    SortingListComponent,
    FormFieldFileComponent,
    FormFieldSelectComponent,
    AdvancedFiltersComponent,
    AdvancedFilterGroupSelectorComponent,
    AdvancedFilterSelectItemComponent,
    ActiveFilterPreviewComponent,
    FilterRangeComponent,
    WallPreviewComponent,
  ],
  exports: [
    BlockComponent,
    MainMenuComponent,
    FooterComponent,
    CookiesAlertComponent,
    PopupComponent,
    WallPreviewComponent,
    SpecialBlockContainerComponent,
    ...directives,
    BlockContainerComponent,
  ],
  providers: [
    PopupService,
    MainMenuService,
    LightboxService,
    ScryptZMailerLite,
    FormService,
    ComponentDependencyJunctionService,
    {provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: {duration: 5000}},
  ],
})
export class CreatorModule {
}
